// src/pages/UpgradePage.jsx
// ✅ Stripe Checkout redirect per Europa
// ✅ Wave / Orange Money / MTN per Africa Occidentale
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { subscriptionApi } from '../api/subscriptionApi';
import PaymentMethodModal from '../components/subscription/PaymentMethodModal';
import {
    SparklesIcon, CheckCircleIcon, ArrowPathIcon,
    ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// =====================================================================
// PREZZI UFFICIALI — sincronizzati con Stripe e DB
// EUR/GBP | GNF (Guinée) | XOF (CFA Sénégal/CI/Mali)
// =====================================================================
const PLANS = [
    {
        id: 'STARTER', name: 'Starter', icon: '🚀', color: 'sky',
        prices: { EUR: { monthly: 29,      yearly: 290 },
                  GBP: { monthly: 25,      yearly: 250 },
                  GNF: { monthly: 330000,  yearly: 3300000 },
                  XOF: { monthly: 19000,   yearly: 190000 } },
        features: ['10 progetti attivi','10 utenti','500 spese/mese',
                   '50 richieste AI/mese','Report PDF','Supporto email'],
    },
    {
        id: 'PRO', name: 'Pro', icon: '⚡', color: 'blue', popular: true,
        prices: { EUR: { monthly: 79,      yearly: 790 },
                  GBP: { monthly: 65,      yearly: 650 },
                  GNF: { monthly: 900000,  yearly: 9000000 },
                  XOF: { monthly: 52000,   yearly: 520000 } },
        features: ['Progetti illimitati','Utenti illimitati','Spese illimitate',
                   '200 richieste AI/mese','Multi-valuta','AI Chat + Analisi costi',
                   'Supporto prioritario'],
    },
    {
        id: 'ENTERPRISE', name: 'Enterprise', icon: '👑', color: 'purple',
        prices: { EUR: { monthly: 149,     yearly: 1490 },
                  GBP: { monthly: 125,     yearly: 1250 },
                  GNF: { monthly: 1700000, yearly: 17000000 },
                  XOF: { monthly: 98000,   yearly: 980000 } },
        features: ['Tutto di Pro +','Storage 100 GB','Supporto 24/7',
                   'White Label','Audit Logs','SLA garantito','Onboarding dedicato'],
    },
];

const CURRENCY_LABELS = { EUR: '€', GBP: '£', XOF: 'CFA ', GNF: 'FG ' };

const PlanCard = ({ plan, currency, billingCycle, current, onUpgrade, loading }) => {
    const isCurrent = current === plan.id;
    const cur = ['EUR','GBP','GNF','XOF'].includes(currency) ? currency : 'EUR';
    const price = plan.prices[cur]?.[billingCycle] ?? 0;

    const colorMap = {
        sky:    { border: 'border-sky-300',    btn: 'bg-sky-500 hover:bg-sky-600' },
        blue:   { border: 'border-blue-300',   btn: 'bg-blue-600 hover:bg-blue-700' },
        purple: { border: 'border-purple-300', btn: 'bg-purple-600 hover:bg-purple-700' },
    };
    const c = colorMap[plan.color] || colorMap.blue;

    return (
        <div className={`relative bg-white rounded-2xl border-2 ${isCurrent ? 'border-emerald-400' : plan.popular ? c.border : 'border-gray-100'} p-6 flex flex-col`}>
            {plan.popular && !isCurrent && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                    Più popolare
                </div>
            )}
            {isCurrent && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    Piano attuale
                </div>
            )}

            <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">{plan.icon}</span>
                <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
            </div>

            <div className="mb-6">
                <div>
                    <span className="text-3xl font-bold text-gray-900">
                        {CURRENCY_LABELS[currency]}{price?.toLocaleString('it-IT')}
                    </span>
                    <span className="text-gray-400 text-sm">/{billingCycle === 'yearly' ? 'anno' : 'mese'}</span>
                    {billingCycle === 'yearly' && (
                        <p className="text-emerald-600 text-xs font-semibold mt-1">2 mesi gratis!</p>
                    )}
                </div>
            </div>

            <ul className="space-y-2.5 flex-1 mb-6">
                {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        {f}
                    </li>
                ))}
            </ul>

            {isCurrent ? (
                <div className="w-full py-2.5 bg-emerald-50 text-emerald-700 rounded-xl text-sm font-semibold text-center">
                    ✅ Attivo
                </div>
            ) : (
                <button
                    onClick={() => onUpgrade(plan.id, billingCycle)}
                    disabled={loading}
                    className={`w-full py-2.5 ${c.btn} text-white rounded-xl text-sm font-semibold transition disabled:opacity-50 flex items-center justify-center gap-2`}>
                    {loading ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : null}
                    Passa a {plan.name}
                </button>
            )}
        </div>
    );
};

const UpgradePage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { organization } = useAuth();

    const [billingCycle, setBillingCycle] = useState('monthly');
    const [loading, setLoading] = useState(false);
    const [currentPlan, setCurrentPlan] = useState(null);

    // Stato modale pagamento
    const [modal, setModal] = useState({ open: false, plan: null, cycle: null });

    const currency = organization?.currency || 'EUR';
    const region   = organization?.region   || 'EUROPE';
    const upgraded = searchParams.get('upgraded');
    const canceled = searchParams.get('canceled');

    useEffect(() => {
        subscriptionApi.getCurrent().then(res => {
            setCurrentPlan(res.data?.subscription?.plan || 'FREE');
        }).catch(() => setCurrentPlan('FREE'));
    }, []);

    useEffect(() => {
        if (upgraded) {
            toast.success('Upgrade completato! Benvenuto nel nuovo piano.');
            navigate('/settings', { replace: true });
        }
        if (canceled) {
            toast.error('Pagamento annullato.');
        }
    }, [upgraded, canceled, navigate]);

    // Step 1: apre il modale per scegliere il provider
    const handleUpgrade = (plan, cycle) => {
        setModal({ open: true, plan, cycle });
    };

    // Step 2: chiamata API dopo che l'utente ha scelto il metodo
    const handlePaymentConfirm = async ({ provider, phone }) => {
        setLoading(true);
        try {
            const res = await subscriptionApi.upgradeToPro({
                plan:         modal.plan,
                billingCycle: modal.cycle,
                provider,
                phone,
            });

            if (res.data?.paymentUrl) {
                // MTN non ha redirect URL — naviga alla success page
                if (res.data?.requiresPolling) {
                    toast.success('Richiesta inviata! Controlla il telefono per confermare.');
                    setModal({ open: false, plan: null, cycle: null });
                    navigate('/settings');
                } else {
                    window.location.href = res.data.paymentUrl;
                }
            } else {
                toast.error('Errore avvio pagamento');
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Errore durante il pagamento');
        } finally {
            setLoading(false);
        }
    };

    // Calcola importo per il modale
    const getModalAmount = () => {
        if (!modal.plan) return 0;
        const planObj = PLANS.find(p => p.id === modal.plan);
        if (!planObj) return 0;
        const cur = ['EUR','GBP','GNF','XOF'].includes(currency) ? currency : 'EUR';
        return planObj.prices[cur]?.[modal.cycle || 'monthly'] ?? 0;
    };

    return (
        <>
        {/* Modale selezione pagamento */}
        <PaymentMethodModal
            isOpen={modal.open}
            onClose={() => setModal({ open: false, plan: null, cycle: null })}
            onConfirm={handlePaymentConfirm}
            loading={loading}
            plan={modal.plan}
            billingCycle={modal.cycle}
            amount={getModalAmount()}
            currency={currency}
            region={region}
        />

        <div className="max-w-5xl mx-auto space-y-8">
            <div className="text-center">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Scegli il tuo piano</h1>
                <p className="text-gray-500">Scala quando vuoi, cancella in qualsiasi momento</p>
            </div>

            {/* Toggle billing */}
            <div className="flex items-center justify-center gap-3">
                <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900' : 'text-gray-400'}`}>Mensile</span>
                <button
                    onClick={() => setBillingCycle(c => c === 'monthly' ? 'yearly' : 'monthly')}
                    className={`relative w-12 h-6 rounded-full transition-colors ${billingCycle === 'yearly' ? 'bg-purple-600' : 'bg-gray-200'}`}>
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${billingCycle === 'yearly' ? 'translate-x-7' : 'translate-x-1'}`} />
                </button>
                <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900' : 'text-gray-400'}`}>
                    Annuale <span className="text-emerald-600 font-bold">-17%</span>
                </span>
            </div>

            {/* Piani */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {PLANS.map(plan => (
                    <PlanCard
                        key={plan.id}
                        plan={plan}
                        currency={currency}
                        billingCycle={billingCycle}
                        current={currentPlan}
                        onUpgrade={handleUpgrade}
                        loading={loading}
                    />
                ))}
            </div>

            {/* Garanzie */}
            <div className="bg-gray-50 rounded-2xl p-6 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                    <ShieldCheckIcon className="w-5 h-5 text-emerald-500" />
                    <span>Pagamento sicuro SSL</span>
                </div>
                <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
                    <span>Cancella quando vuoi</span>
                </div>
                <div className="flex items-center gap-2">
                    <SparklesIcon className="w-5 h-5 text-emerald-500" />
                    <span>14 giorni di trial gratuito</span>
                </div>
            </div>

            {/* Provider pagamento */}
            <p className="text-center text-xs text-gray-400">
                Europa: 💳 <strong>Stripe</strong> · Africa: 🌊 <strong>Wave</strong> · 🟠 <strong>Orange Money</strong> · 🟡 <strong>MTN MoMo</strong>
            </p>
        </div>
        </>
    );
};

export default UpgradePage;